import { Component, OnDestroy, OnInit } from '@angular/core';
import { OrganisationUnitService } from '../../../services/organisation-unit.service';
import { catchError, first, forkJoin, map, of, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { OrganisationUnitModel } from '../../../models/organisation-unit/organisation-unit.model';
import { OrganisationUnitTypeModel } from '../../../models/organisation-unit/organisation-unit-type.model';
import { LoadingService } from '../../../services/loading.service';
import { editIcon } from '../../../app-icons';
import { DxTreeListTypes } from 'devextreme-angular/ui/tree-list';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { UserContextService } from 'src/app/services/user-context.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-organisation-unit-overview',
  templateUrl: './organisation-unit-overview.component.html',
  styleUrls: ['./organisation-unit-overview.component.scss'],
  standalone: false
})
export class OrganisationUnitOverviewComponent implements OnInit, OnDestroy {
  selectedOrganisationUnits: Array<OrganisationUnitModel> = [];
  private subs = new Subscription();
  organisationUnits: OrganisationUnitModel[] = [];
  OrganisationUnitTypeModel = OrganisationUnitTypeModel;
  selectedRowKeys: any[] = [];

  showEmployeesExistsModal: boolean = false;
  modalEmployeesExistsOptions: ConfirmationDialogData | null = null;

  showDeleteOragisationUnitModal: boolean = false;
  modalConfirmationOptions: ConfirmationDialogData | null = null;

  unitsToDelete: OrganisationUnitModel[] = [];

  isManager: boolean = false;

  readonly stateKey: string = "organisation-units-overview"

  get editIcon() {
    return editIcon;
  }

  get selectionMode() {
    return this.isManager ? 'none' : 'multiple';
  }

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private router: Router,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private userContextService: UserContextService,
    private pageStateService: PageStateService
  ) {
  }

  ngOnInit(): void {
    this.isManager = this.userContextService.isManager;

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreOrganisationUnitsOverview
    )) {
      this.pageStateService.clear(this.stateKey);
    }

    this.loadOrganisationUnits();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  loadOrganisationUnits(): void {
    const obs = this.organisationUnitService.getAllOrganisationUnits();
    const sub = this.loadingService.load(obs).subscribe((organisationUnits) => {
      this.organisationUnits = organisationUnits;
      this.organisationUnits[0].parentId = null;
    });
    this.subs.add(sub);
  }

  isDeleteDisabled(): boolean {
    if (this.selectedOrganisationUnits.length < 1) return true;

    const rootUnit = this.organisationUnits[0];
    return this.selectedOrganisationUnits.some(
      (unit) => unit.id === rootUnit.id
    );
  }

  onUnitsSelectionChanged(e: DxTreeListTypes.SelectionChangedEvent): void {
    this.selectedOrganisationUnits = e.component.getSelectedRowsData('all');
    console.log('selectedData: ', this.selectedOrganisationUnits);
  }

  onAddClick(): void {
    this.router.navigate(['/organisation-unit/add'], {
      queryParams: { parent: this.selectedOrganisationUnits[0]?.id },
    });
  }

  onDeleteClick(): void {
    if (!this.selectedOrganisationUnits.length) return;

    const requests = this.selectedOrganisationUnits.map((unit) =>
      this.organisationUnitService.getOrganizationUnitPersons(unit.id).pipe(
        catchError(() => of([])),
        map((persons) => ({ unit, persons }))
      )
    );

    this.loadingService.load(forkJoin(requests)).subscribe((results) => {
      const unitsWithPersons = results.filter(
        (result) => result.persons.length > 0
      );

      if (unitsWithPersons.length > 0) {
        this.showEmployeesExistsModal = true;

        this.modalEmployeesExistsOptions = {
          hideUndo: true,
          submitButtonText: this.translateService.instant('COMMON.CLOSE'),
          message: this.translateService.instant(
            'STRUCTURE.OVERVIEW.ASSIGNED-EMPLOYEES-EXISTS'
          ),
        };
      } else {
        const allSelectedIds = this.selectedOrganisationUnits.map((u) => u.id);
        const unitMap = new Map(this.organisationUnits.map((u) => [u.id, u]));

        function hasSelectedAncestor(unit: OrganisationUnitModel): boolean {
          let parentId: any = unit.parentId;
          while (parentId) {
            if (allSelectedIds.includes(parentId)) return true;
            parentId = unitMap.get(parentId)?.parentId;
          }
          return false;
        }

        this.unitsToDelete = this.selectedOrganisationUnits.filter(
          (unit) => !hasSelectedAncestor(unit)
        );

        this.modalConfirmationOptions = {
          message: this.translateService.instant(
            'STRUCTURE.OVERVIEW.DELETE-CONFIRMATION'
          ),
          submitButtonText: this.translateService.instant('COMMON.DELETE'),
          dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
          hideUndo: true,
        };
        this.showDeleteOragisationUnitModal = true;
      }
    });
  }

  onEditClick(id: any): void {
    this.router.navigate(['organisation-unit/edit/', id]);
  }

  onOrganisationUnitsInitialized(event: any) {
    event.component.forEachNode((node: any) => {
      if (node.children) {
        node.data.name = node.data.name.replace(/\s\(\d+\)$/, '');
        node.data.name += ` (${node.children.length})`;
      }
    });
  }

  onRemoveOragisationUnitConfirm() {
    this.showEmployeesExistsModal = false;
  }

  confirmOrganisationUnitDeletion(
    unitsToDelete: OrganisationUnitModel[]
  ): void {
    const idsToDelete = unitsToDelete.map((unit) => unit.id);

    this.loadingService
      .load(this.organisationUnitService.deleteOrganisationUnits(idsToDelete))
      .subscribe(() => {
        this.loadOrganisationUnits();
        this.showDeleteOragisationUnitModal = false;
      });
  }

  saveGridState = (state: any): Promise<void> => {
    if (this.stateKey) {
      this.pageStateService.save(this.stateKey, state);
    }

    return Promise.resolve();
  };

  loadGridState = (): Promise<any> => {
    if (!this.stateKey) {
      return Promise.resolve({});
    }

    const state = 
      this.pageStateService.load<any>(this.stateKey);
    if (!state) return Promise.resolve({});

    return Promise.resolve(state);
  };
}
